/**
 * Création d'une propriété combinée : plusieurs propriétés analytiques lues comme une seule.
 *
 * Le cas qui l'a motivée : un site web et son application mobile sont deux propriétés Piano
 * distinctes alors qu'elles décrivent la même activité. Le dashboard ne sachant lire qu'une
 * propriété, il fallait choisir et perdre l'autre.
 *
 * **Le rôle des membres n'est pas symétrique**, et l'écran doit le dire : la propriété
 * principale alimente tous les blocs habituels — leviers, régies, campagnes ; chaque autre
 * reçoit un bloc de premier niveau à elle. C'est ce qui permet de voir la contribution de
 * l'app sans la fondre dans le trafic du site.
 */

import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Layers, X, Loader } from 'lucide-react';
import config from '../../config';

const API_URL = config.API_URL;

/** Libellé proposé d'office : le cas d'usage qui a motivé l'écran. */
const LIBELLE_PAR_DEFAUT = 'Application mobile';

const ProprieteCombineeModal = ({ ouverte, onFermer, onCreee, token, proprietes = [], typeMesure }) => {
  const [nom, setNom] = useState('');
  const [principalId, setPrincipalId] = useState('');
  const [complements, setComplements] = useState({});
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreur, setErreur] = useState('');

  /**
   * Seules les propriétés d'une même solution de mesure se combinent — le tableau interroge
   * une API, pas deux. Google Analytics en est exclu : ses sessions se dédupliquent, les
   * additionner surestimerait le trafic. Le serveur applique la même règle.
   */
  const candidates = useMemo(() => (
    proprietes.filter(p => !p.combinee && p.type !== 'GOOGLE_ANALYTICS'
      && (!typeMesure || p.type === typeMesure))
  ), [proprietes, typeMesure]);

  const reinitialiser = () => {
    setNom('');
    setPrincipalId('');
    setComplements({});
    setErreur('');
  };

  const basculer = (id) => setComplements(actuels => {
    const suivants = { ...actuels };
    if (suivants[id] !== undefined) delete suivants[id];
    else suivants[id] = LIBELLE_PAR_DEFAUT;
    return suivants;
  });

  const choisis = Object.keys(complements);
  const pretAEnvoyer = nom.trim() && principalId && choisis.length > 0;

  const enregistrer = async () => {
    if (!pretAEnvoyer) return;
    setEnregistrement(true);
    setErreur('');

    try {
      const reponse = await axios.post(`${API_URL}/api/dashboard/consolide/proprietes-combinees`, {
        name: nom.trim(),
        membres: [
          { itemId: principalId, principal: true },
          ...choisis.map(id => ({ itemId: id, libelle: complements[id], principal: false }))
        ]
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (reponse.data?.success) {
        reinitialiser();
        onCreee?.();
        onFermer();
      } else {
        setErreur(reponse.data?.error || 'La création a échoué.');
      }
    } catch (e) {
      setErreur(e.response?.data?.error || 'La création a échoué.');
    } finally {
      setEnregistrement(false);
    }
  };

  if (!ouverte) return null;

  return ReactDOM.createPortal(
    <div
      onClick={onFermer}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: '20px'
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '620px',
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '18px 20px', borderBottom: '1px solid #e5e7eb'
        }}>
          <Layers size={18} style={{ color: '#7c2d12' }} />
          <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600 }}>Combiner des propriétés</h3>
          <button
            onClick={onFermer}
            style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}
          >
            <X size={18} style={{ color: '#6b7280' }} />
          </button>
        </div>

        <div style={{ padding: '20px', overflowY: 'auto' }}>
          <p style={{ margin: '0 0 18px', fontSize: '13px', color: '#6b7280', lineHeight: 1.5 }}>
            La propriété principale alimente les blocs habituels — leviers, régies, campagnes.
            Chaque propriété ajoutée reçoit un bloc à elle, et les résultats globaux les
            réunissent.
          </p>

          {erreur && (
            <div style={{
              padding: '10px 12px', borderRadius: '8px', backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5', color: '#991b1b', fontSize: '13px', marginBottom: '16px'
            }}>
              {erreur}
            </div>
          )}

          <label style={etiquette}>Nom</label>
          <input
            value={nom}
            onChange={e => setNom(e.target.value)}
            placeholder="BZB — site et application"
            style={{ ...champ, marginBottom: '18px' }}
          />

          <label style={etiquette}>Propriété principale</label>
          <select
            value={principalId}
            onChange={e => { setPrincipalId(e.target.value); basculerRetrait(e.target.value, setComplements); }}
            style={{ ...champ, marginBottom: '18px', cursor: 'pointer' }}
          >
            <option value="">Choisir…</option>
            {candidates.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>

          <label style={etiquette}>Propriétés ajoutées</label>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            {candidates.filter(p => p.id !== principalId).map((p, i) => {
              const coche = complements[p.id] !== undefined;
              return (
                <div
                  key={p.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px',
                    backgroundColor: i % 2 === 0 ? 'white' : '#FAF9F5',
                    borderBottom: '1px solid #f3f4f6'
                  }}
                >
                  <input type="checkbox" checked={coche} onChange={() => basculer(p.id)} />
                  <span style={{ fontSize: '14px', flex: 1 }}>{p.name}</span>
                  {/* Le libellé titre le bloc du tableau : c'est ce que l'utilisateur lira,
                      pas le nom technique de la propriété. */}
                  {coche && (
                    <input
                      value={complements[p.id]}
                      onChange={e => setComplements(a => ({ ...a, [p.id]: e.target.value }))}
                      placeholder="Titre du bloc"
                      style={{ ...champ, width: '190px', padding: '6px 8px', fontSize: '13px' }}
                    />
                  )}
                </div>
              );
            })}
            {candidates.filter(p => p.id !== principalId).length === 0 && (
              <div style={{ padding: '14px 12px', fontSize: '13px', color: '#9ca3af' }}>
                Aucune autre propriété à combiner.
              </div>
            )}
          </div>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: '10px',
          padding: '14px 20px', borderTop: '1px solid #e5e7eb'
        }}>
          <button onClick={onFermer} disabled={enregistrement} style={boutonSecondaire}>Annuler</button>
          <button
            onClick={enregistrer}
            disabled={!pretAEnvoyer || enregistrement}
            style={{ ...boutonPrincipal, opacity: (!pretAEnvoyer || enregistrement) ? 0.5 : 1 }}
          >
            {enregistrement && <Loader size={14} className="spin" />}
            Créer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/** La propriété promue principale ne peut pas rester dans les propriétés ajoutées. */
const basculerRetrait = (id, setComplements) => setComplements(actuels => {
  if (actuels[id] === undefined) return actuels;
  const suivants = { ...actuels };
  delete suivants[id];
  return suivants;
});

const etiquette = {
  display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px'
};

const champ = {
  width: '100%', padding: '9px 11px', fontSize: '14px',
  border: '1px solid #d1d5db', borderRadius: '6px', outline: 'none', boxSizing: 'border-box'
};

const boutonSecondaire = {
  padding: '9px 16px', border: '1px solid #d1d5db', borderRadius: '6px',
  backgroundColor: 'white', fontSize: '14px', cursor: 'pointer'
};

const boutonPrincipal = {
  display: 'flex', alignItems: 'center', gap: '6px',
  padding: '9px 18px', border: 'none', borderRadius: '6px',
  backgroundColor: '#7c2d12', color: 'white', fontSize: '14px', fontWeight: 500, cursor: 'pointer'
};

export default ProprieteCombineeModal;
